"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef } from "react";
import { BlobImage } from "@/components/BlobImage";
import { useImagePreview } from "@/hooks/useImagePreview";
import { moveRect, resizeRect, type Corner, type NormRect } from "@/lib/image/crop";
import { rotatedSize, type Rotation } from "@/lib/image/job";
import type { Size } from "@/lib/image/size";

/** Longest side of the on-screen copy of the photo. */
const DISPLAY_MAX = 1200;
const CORNERS: {
  corner: Corner;
  className: string;
}[] = [{
  corner: "nw",
  className: "-top-[22px] -left-[22px] cursor-nwse-resize"
}, {
  corner: "ne",
  className: "-top-[22px] -right-[22px] cursor-nesw-resize"
}, {
  corner: "sw",
  className: "-bottom-[22px] -left-[22px] cursor-nesw-resize"
}, {
  corner: "se",
  className: "-right-[22px] -bottom-[22px] cursor-nwse-resize"
}];
type Drag = {
  mode: "move" | Corner;
  startX: number;
  startY: number;
  start: NormRect;
};

/** The photo (turned and flipped as it will be saved) with a crop frame on top. */
export function CropEditor(t0) {
  const $ = _c(44);
  const {
    file,
    source,
    rotate,
    flipH,
    flipV,
    rect,
    ratio,
    onRect
  } = t0;
  const display = rotatedSize(source, rotate);
  const scale = Math.min(1, DISPLAY_MAX / Math.max(display.width, display.height));
  const t1 = Math.max(1, Math.round(display.width * scale));
  const t2 = Math.max(1, Math.round(display.height * scale));
  let t3;
  if ($[0] !== flipH || $[1] !== flipV || $[2] !== rotate || $[3] !== t1 || $[4] !== t2) {
    t3 = {
      format: "png",
      rotate,
      flipH,
      flipV,
      width: t1,
      height: t2
    };
    $[0] = flipH;
    $[1] = flipV;
    $[2] = rotate;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const preview = useImagePreview(file, t3);
  const box = useRef(null);
  const drag = useRef(null);
  const aspect = display.width / display.height;
  const start = function start(mode, e) {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      start: rect
    };
  };
  const move = function move(e_0) {
    const d = drag.current;
    const area = box.current?.getBoundingClientRect();
    if (!d || !area) {
      return;
    }
    const dx = (e_0.clientX - d.startX) / area.width;
    const dy = (e_0.clientY - d.startY) / area.height;
    onRect(d.mode === "move" ? moveRect(d.start, dx, dy) : resizeRect(d.start, d.mode, dx, dy, display, ratio));
  };
  const end = function end() {
    drag.current = null;
  };
  const onKey = function onKey(e_1) {
    const keys = {
      ArrowLeft: [-0.01, 0],
      ArrowRight: [0.01, 0],
      ArrowUp: [0, -0.01],
      ArrowDown: [0, 0.01]
    };
    const delta = keys[e_1.key];
    if (!delta) {
      return;
    }
    e_1.preventDefault();
    onRect(e_1.shiftKey ? resizeRect(rect, "se", delta[0], delta[1], display, ratio) : moveRect(rect, delta[0], delta[1]));
  };
  const pct = _temp;
  const t4 = "flex justify-center rounded-[10px] bg-[#e6e9f2] p-6";
  const t5 = "relative select-none";
  const t6 = `min(100%, calc(60vh * ${aspect}))`;
  const t7 = `${display.width} / ${display.height}`;
  let t8;
  if ($[6] !== t6 || $[7] !== t7) {
    t8 = {
      width: t6,
      aspectRatio: t7
    };
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== preview.busy || $[10] !== preview.output) {
    t9 = preview.output ? <BlobImage blob={preview.output.blob} alt="Photo to crop" className={`absolute inset-0 size-full ${preview.busy ? "opacity-60" : ""}`} /> : <div className="absolute inset-0 animate-pulse bg-sand" />;
    $[9] = preview.busy;
    $[10] = preview.output;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = pct(rect.x);
  const t11 = pct(rect.y);
  const t12 = pct(rect.w);
  const t13 = pct(rect.h);
  let t14;
  if ($[12] !== t10 || $[13] !== t11 || $[14] !== t12 || $[15] !== t13) {
    t14 = <div aria-hidden="true" className="pointer-events-none absolute shadow-[0_0_0_9999px_rgba(31,36,48,0.55)]" style={{
      left: t10,
      top: t11,
      width: t12,
      height: t13
    }} />;
    $[12] = t10;
    $[13] = t11;
    $[14] = t12;
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== t14 || $[18] !== t9) {
    t15 = <div className="absolute inset-0 overflow-hidden">{t9}{t14}</div>;
    $[17] = t14;
    $[18] = t9;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const t16 = "group";
  const t17 = 0;
  const t18 = "Crop frame. Arrow keys move it; Shift and arrow keys resize it.";
  const t19 = "crop-frame";
  const t20 = e_2 => start("move", e_2);
  const t21 = "absolute cursor-move touch-none border-2 border-white focus-visible:outline-brand";
  const t22 = pct(rect.x);
  const t23 = pct(rect.y);
  const t24 = pct(rect.w);
  const t25 = pct(rect.h);
  let t26;
  if ($[20] !== t22 || $[21] !== t23 || $[22] !== t24 || $[23] !== t25) {
    t26 = {
      left: t22,
      top: t23,
      width: t24,
      height: t25
    };
    $[20] = t22;
    $[21] = t23;
    $[22] = t24;
    $[23] = t25;
    $[24] = t26;
  } else {
    t26 = $[24];
  }
  let t27;
  let t28;
  let t29;
  let t30;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <span className="pointer-events-none absolute inset-y-0 left-1/3 w-px bg-white/50" />;
    t28 = <span className="pointer-events-none absolute inset-y-0 left-2/3 w-px bg-white/50" />;
    t29 = <span className="pointer-events-none absolute inset-x-0 top-1/3 h-px bg-white/50" />;
    t30 = <span className="pointer-events-none absolute inset-x-0 top-2/3 h-px bg-white/50" />;
    $[25] = t27;
    $[26] = t28;
    $[27] = t29;
    $[28] = t30;
  } else {
    t27 = $[25];
    t28 = $[26];
    t29 = $[27];
    t30 = $[28];
  }
  const t32 = CORNERS.map(t31 => {
    const {
      corner,
      className
    } = t31;
    return <span key={corner} data-corner={corner} aria-hidden="true" onPointerDown={e_3 => start(corner, e_3)} className={`absolute flex size-11 touch-none items-center justify-center ${className}`}><span className="size-3.5 rounded-sm border-2 border-ink bg-white" /></span>;
  });
  let t33;
  if ($[29] !== onKey || $[30] !== t20 || $[31] !== t26 || $[32] !== t27 || $[33] !== t28 || $[34] !== t29 || $[35] !== t30 || $[36] !== t32) {
    t33 = <div role={t16} tabIndex={t17} aria-label={t18} data-testid={t19} onPointerDown={t20} onKeyDown={onKey} className={t21} style={t26}>{t27}{t28}{t29}{t30}{t32}</div>;
    $[29] = onKey;
    $[30] = t20;
    $[31] = t26;
    $[32] = t27;
    $[33] = t28;
    $[34] = t29;
    $[35] = t30;
    $[36] = t32;
    $[37] = t33;
  } else {
    t33 = $[37];
  }
  let t34;
  if ($[38] !== end || $[39] !== move || $[40] !== t15 || $[41] !== t33 || $[42] !== t8) {
    t34 = <div className={t4}><div ref={box} className={t5} style={t8} onPointerMove={move} onPointerUp={end} onPointerCancel={end}>{t15}{t33}</div></div>;
    $[38] = end;
    $[39] = move;
    $[40] = t15;
    $[41] = t33;
    $[42] = t8;
    $[43] = t34;
  } else {
    t34 = $[43];
  }
  return t34;
}
function _temp(v) {
  return `${v * 100}%`;
}